import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type Variant = "info" | "success" | "warn" | "error";

interface ToastItem {
  id: number;
  variant: Variant;
  message: string;
}

interface ContextValue {
  push: (variant: Variant, message: string) => void;
}

const Context = createContext<ContextValue | null>(null);

export function useToast() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  // Memoise so consumers can put `toast` in useEffect deps without re-firing
  // on every render. ctx.push is stable (useCallback in ToastProvider), so
  // the returned object identity is also stable for the lifetime of the
  // provider.
  return useMemo(
    () => ({
      info: (m: string) => ctx.push("info", m),
      success: (m: string) => ctx.push("success", m),
      warn: (m: string) => ctx.push("warn", m),
      error: (m: string) => ctx.push("error", m),
    }),
    [ctx],
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: Variant, message: string) => {
      const id = Date.now() + Math.random();
      setToasts((list) => [...list, { id, variant, message }]);
      window.setTimeout(() => dismiss(id), variant === "error" ? 8000 : 4500);
    },
    [dismiss],
  );

  return (
    <Context.Provider value={{ push }}>
      {children}
      <div className="toast__container" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.variant}`}>
            <div className="toast__msg">{t.message}</div>
            <button
              className="toast__dismiss"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </Context.Provider>
  );
}
